import { type ReactNode, useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Code,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconCode } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { DateField } from '../../shared/ui/DateField';

export interface AuditColumn<T> {
  header: string;
  render(row: T): ReactNode;
  width?: number | string;
}

interface Props<T> {
  title: string;
  clause: string;
  description?: ReactNode;
  fetcher: (filters: { fromDate?: string; toDate?: string }) => Promise<T[]>;
  columns: AuditColumn<T>[];
  /** Колбэк для просмотра полного JSON-payload (для RegistrationEvent, AuditLog). */
  expandable?: boolean;
  getExpandData?: (row: T) => unknown;
}

/**
 * Универсальная таблица для аудит-страницы:
 * - Заголовок с пунктом ПП 1317.
 * - Фильтр по периоду (fromDate / toDate, формат YYYY-MM-DD).
 * - Кнопка «назад» в сводку.
 * - Опциональная expand-модалка для JSON-payload'ов.
 */
export function AuditTable<T extends { id: string }>({
  title,
  clause,
  description,
  fetcher,
  columns,
  expandable,
  getExpandData,
}: Props<T>) {
  const [rows, setRows] = useState<T[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [expanded, setExpanded] = useState<T | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await fetcher({
        fromDate: fromDate ? new Date(fromDate + 'T00:00:00').toISOString() : undefined,
        toDate: toDate ? new Date(toDate + 'T23:59:59').toISOString() : undefined,
      });
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  return (
    <Stack>
      <Group>
        <Anchor component={Link} to="/audit">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            <Text size="sm">К сводке аудита</Text>
          </Group>
        </Anchor>
      </Group>
      <Group justify="space-between">
        <div>
          <Title order={2}>{title}</Title>
          <Text c="dimmed" size="sm">
            ПП РФ № 1317, {clause}
          </Text>
        </div>
        <Group>
          <DateField label="С даты" value={fromDate} onChange={setFromDate} size="xs" />
          <DateField label="По дату" value={toDate} onChange={setToDate} size="xs" />
        </Group>
      </Group>

      {description && (
        <Alert color="blue" variant="light">
          {description}
        </Alert>
      )}

      {error && <Alert color="red">{error}</Alert>}

      <Table striped withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            {columns.map((c) => (
              <Table.Th key={c.header} style={c.width ? { width: c.width } : undefined}>
                {c.header}
              </Table.Th>
            ))}
            {expandable && <Table.Th style={{ width: 40 }} />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.id}>
              {columns.map((c) => (
                <Table.Td key={c.header}>{c.render(r)}</Table.Td>
              ))}
              {expandable && (
                <Table.Td>
                  <ActionIcon variant="subtle" onClick={() => setExpanded(r)}>
                    <IconCode size={14} />
                  </ActionIcon>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
          {rows.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={columns.length + (expandable ? 1 : 0)}>
                <Text c="dimmed" ta="center" py="md">
                  {loading ? 'Загрузка…' : 'Записей не найдено'}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Text size="xs" c="dimmed">
        Показано {rows.length} записей. Лимит на запрос: 500–1000.
      </Text>

      <Modal
        opened={!!expanded}
        onClose={() => setExpanded(null)}
        title="Полная запись"
        size="lg"
      >
        {expanded && (
          <Code block style={{ maxHeight: 500, overflow: 'auto' }}>
            {JSON.stringify(getExpandData ? getExpandData(expanded) : expanded, null, 2)}
          </Code>
        )}
      </Modal>
    </Stack>
  );
}
